import { useTranslation } from 'react-i18next';
import type { VendorKpiCategory } from '@/types/vendor-kpi.ts';
import { getScoreColor, getCardTier } from '@/types/vendor-kpi.ts';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';

interface VendorCardHeroProps {
  name: string;
  initials: string;
  globalScore: number;
  categoryBadge?: string;
  categoryColor?: string;
  categories: VendorKpiCategory[];
  totalSimulations?: number;
}

export default function VendorCardHero({
  name,
  initials,
  globalScore,
  categoryBadge,
  categoryColor,
  categories,
  totalSimulations,
}: VendorCardHeroProps) {
  const { t } = useTranslation();
  const tier = getCardTier(globalScore);
  const scoreColor = getScoreColor(globalScore);

  const wrapperClass =
    tier === 'elite'
      ? 'p-[1.5px] rounded-2xl bg-gradient-to-br from-primary via-[#C9A84C] to-primary card-glow'
      : '';

  const innerBorder =
    tier === 'pro'
      ? 'border border-primary/40'
      : tier !== 'elite'
        ? 'border border-border'
        : '';

  const inner = (
    <div className={`rounded-2xl ${innerBorder} bg-card p-6`}>
      {/* Score + Badge */}
      <div className="mb-4 flex items-center justify-between">
        <span className="text-5xl font-extrabold" style={{ color: scoreColor }}>
          {globalScore}
        </span>
        {categoryBadge && (
          <Badge
            variant="outline"
            className="rounded-full text-xs font-bold"
            style={{
              backgroundColor: `${categoryColor ?? 'var(--primary)'}20`,
              color: categoryColor ?? 'var(--primary)',
              borderColor: 'transparent',
            }}
          >
            {categoryBadge}
          </Badge>
        )}
      </div>

      {/* Avatar */}
      <div className="mb-4 flex justify-center">
        <Avatar className="size-20">
          <AvatarFallback
            className="text-2xl font-bold text-white"
            style={{ backgroundColor: categoryColor ?? 'var(--primary)' }}
          >
            {initials}
          </AvatarFallback>
        </Avatar>
      </div>

      {/* Name */}
      <p className="mb-4 text-center text-base font-bold uppercase tracking-wider text-foreground">
        {name}
      </p>

      {/* 6 stats */}
      <div className="space-y-2">
        {categories.map((cat) => (
          <div key={cat.id} className="flex items-center gap-2">
            <span className="w-8 text-right text-sm font-extrabold" style={{ color: getScoreColor(cat.score) }}>
              {cat.score}
            </span>
            <span className="w-8 text-xs text-muted-foreground">{cat.shortName}</span>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-border">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${(cat.score / 99) * 100}%`,
                  backgroundColor: getScoreColor(cat.score),
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      {totalSimulations !== undefined && (
        <div className="mt-4 border-t border-border pt-3 text-center text-xs text-muted-foreground">
          {t('vendor.simulationsCompleted', { count: totalSimulations })}
        </div>
      )}
    </div>
  );

  if (tier === 'elite') {
    return <div className={wrapperClass}>{inner}</div>;
  }

  return inner;
}
